import axios from "axios";
import { compressFormDataImages } from "./utils/imageCompression";


const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002"; // Fallback to localhost if not set
// User API functions
export async function getUsers() {
  const response = await axios.get(`${URL}/users`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function getUserById(id) {
  try {
    const response = await axios.get(`${URL}/users/getUser`, {
      params: { id }, // ✅ Send ID as query param
    });

    if (response.status === 200) {
      return response.data; // ✅ Return user data
    } else {
      return null;
    }
  } catch (error) {
    console.error("⛔ Error fetching user by ID:", error);
    return null;
  }
}

export async function createUser(user) {
  const response = await axios.post(`${URL}/users`, user);
  return response;
}

export async function resetPassword(tokenId, token, newPassword) {
  try {
    const response = await axios.post(`${URL}/users/changePassword`, {
      tokenId,
      token,
      newPassword,
    });
    return response;
  } catch (error) {
    console.error("⛔ Error changing password:", error);
    throw error;
  }
}


// Send password reset email
export async function sendPasswordResetEmail(email) {
  try {
    const response = await axios.post(`${URL}/users/sendPasswordReset`, {
      email,
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error sending password reset email:", error);
    return { error: "Failed to send email" };
  }
}

// Send password reset email
export async function sendPasswordConfirmation(email) {
  try {
    const response = await axios.post(`${URL}/users/sendPasswordConfirmation`, {
      email,
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error sending password reset email:", error);
    return { error: "Failed to send email" };
  }
}

// Get user email by id
export async function getUserEmailById(id) {
  try {
    const response = await axios.post(`${URL}/users/email`, { id });
    return response.data;
  } catch (error) {
    console.error("⛔ Error fetching user ID by email:", error);
    return { error: "Failed to fetch user ID" };
  }
}

export async function updateUser(id, user) {
  const response = await axios.put(`${URL}/users/${id}`, user);
  return response;
}

export async function updateUserId(id, practice) {
  const response = await axios.put(`${URL}/users/${id}/practice`, { practice });
  return response;
}

//Update user profile picture
export async function updateUserProfilePicURL(id, ppicURL) {
  try {
    const response = await axios.put(`${URL}/user/${id}/ppicURL`, { ppicURL });
    console.log("✅ API Response:", response.data);

    if (response.status !== 200) {
      throw new Error("Failed to update profile picture.");
    }

    return response.data;
  } catch (error) {
    console.error("API Error in updateUserProfilePicURL:", error);
    throw error;
  }
}

// Update the medical aid details for all patients on an account and on the account itself
export async function bulkUpdatePatients(patientIds, accountId, data, dependantList) {
  try {
    // ✅ Normalize patientIds -> array of string IDs
    const ids = Array.isArray(patientIds)
      ? patientIds
          .map((p) => (typeof p === "string" ? p : p?._id))
          .filter(Boolean)
      : typeof patientIds === "string"
      ? [patientIds]
      : patientIds?._id
      ? [patientIds._id]
      : [];

    if (!ids.length) {
      throw new Error("bulkUpdatePatients: patientIds must be a non-empty array (or a single id).");
    }
    if (!accountId) {
      throw new Error("bulkUpdatePatients: accountId is required.");
    }

    const queryString = encodeURIComponent(ids.join(","));

    const payload = {
      ...(data || {}),
      dependantList: dependantList || [], // ✅ keep route happy
    };

    const response = await axios.put(
      `${URL}/patients/bulkupdate/${accountId}?patientIds=${queryString}`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "⛔ Error updating patients & account:",
      error?.response?.data || error.message
    );
    throw error;
  }
}


// Update the practice logo URL for a user
export async function updatePracticeURL(id, logoURL) {
  const response = await axios.put(`${URL}/practice/${id}/logoURL`, {
    logoURL,
  });
  return response;
}

// Soft delete: the backend flags the user as deleted (no more logins, hidden
// from user lists) but keeps the document so history stays intact.
export async function deleteUser(id) {
  const response = await axios.delete(`${URL}/users/${id}`);
  return response;
}

export async function verifyUser(user) {
  const res = await axios.post(`${URL}/users/login`, user);
  return res.data; // ✅ { success, token }
}
// Don't use getPracticesByUserId, use fetchallUsersByPractices instead
export async function getUsersByPracticeNumber(practiceNumber) {
  try {
    const response = await axios.get(`${URL}/users/practice/${practiceNumber}`);
    if (response.status === 200) {
      return response.data; // Return the data if the request was successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching users by practice number:", error);
    return null; // Return null or handle errors appropriately
  }
}

// Get the practice array with its practiceID and practiceName for a single user.
// By default the backend intersects with the caller's JWT-scoped practice array,
// so an active-practice scope automatically narrows every consumer's dropdown.
// Pass { includeAll: true } to bypass the intersection — honoured by the
// backend only when the caller asks about their OWN record (used by the
// BootNav practice switcher so users can still see all practices to switch to).
export async function getUserPracticeById(userId, { includeAll = false } = {}) {
  try {
    const url = includeAll
      ? `${URL}/users/${userId}/practice?includeAll=true`
      : `${URL}/users/${userId}/practice`;
    const response = await axios.get(url);
    if (response.status === 200) {
      return response.data; // Return the 'practice' array if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching user's practice:", error);
    return null; // Handle errors appropriately
  }
}

// Scope (or unscope with null) the session to a single practice.
// Updates sessionStorage with the new JWT on success.
export async function scopePractice(practiceNumber) {
  try {
    const response = await axios.post(`${URL}/users/scope-practice`, {
      practiceNumber: practiceNumber ?? null,
    });
    if (response.status === 200 && response.data?.token) {
      sessionStorage.setItem("user", response.data.token);
      return { success: true };
    }
    return { success: false, error: "No token returned" };
  } catch (error) {
    console.error("Error scoping practice:", error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
}

// Update the user's UI preferences (auto-confirm appointments, auto-complete
// appointments, etc.). Mirrors scopePractice: the backend re-signs the JWT
// with the updated user doc so the change flows into sessionStorage without
// a logout. Pass any subset of the supported keys.
export async function updateUserPreferences(prefs) {
  try {
    const response = await axios.post(`${URL}/users/me/preferences`, prefs);
    if (response.status === 200 && response.data?.token) {
      sessionStorage.setItem("user", response.data.token);
      return { success: true };
    }
    return { success: false, error: "No token returned" };
  } catch (error) {
    console.error("Error updating user preferences:", error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
}

// Practice API functions

export async function getPractice() {
  const response = await axios.get(`${URL}/practice`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function getPracticeById(id) {
  try {
  const response = await axios.get(`${URL}/practice/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching practice by ID:", error);
    return null;
  }
}

export async function getPracticesByNumbers(practiceNumbers) {
  try {
    const response = await axios.post(`${URL}/practice/names`, {
      practiceNumbers,
    });

    if (response.status === 200) {
      return response.data; // ✅ Return the fetched practices
    } else {
      return []; // ✅ Return empty array if request fails
    }
  } catch (error) {
    console.error("Error fetching practices:", error);
    throw new Error(
      error.response?.data?.message || "Failed to fetch practices"
    );
  }
}

// Get the discipline codes
export async function getDisciplineCodes() {
  try {
    const response = await axios.get(`${URL}/disciplines`);

    if (response.status === 200) {
      return response.data; // ✅ Return the fetched practices
    } else {
      return []; // ✅ Return empty array if request fails
    }
  } catch (error) {
    console.error("Error fetching disciplines:", error);
    throw new Error(
      error.response?.data?.message || "Failed to fetch disciplines"
    );
  }
}

export async function getPracticesInfoByNumbers(practiceNumbers) {
  try {
    const response = await axios.post(`${URL}/practice`, {
      practiceNumbers,
    });

    if (response.status === 200) {
      return response.data; // ✅ Return the fetched practices
    } else {
      return []; // ✅ Return empty array if request fails
    }
  } catch (error) {
    console.error("Error fetching practices:", error);
    throw new Error(
      error.response?.data?.message || "Failed to fetch practices"
    );
  }
}

export async function createPractice(practice) {
  const response = await axios.post(`${URL}/practice`, practice);
  return response;
}

export async function updatePractice(id, practice) {
  const response = await axios.put(`${URL}/practice/${id}`, practice);
  return response;
}

export async function deletePractice(id) {
  const response = await axios.delete(`${URL}/practice/${id}`);
  return response;
}

// Service API functions

export async function getService() {
  const response = await axios.get(`${URL}/service`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function getServiceById(id) {
  const response = await axios.get(`${URL}/services/${id}`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function createService(service) {
  const response = await axios.post(`${URL}/services`, service);
  return response;
}

export async function updateService(id, service) {
  const response = await axios.put(`${URL}/services/${id}`, service);
  return response;
}

export async function deleteService(id) {
  const response = await axios.delete(`${URL}/services/${id}`);
  return response;
}

// Restore a soft-deleted service. Mirrors deleteService — pairs with the
// "Show deleted" toggle on the services settings page.
export async function restoreService(id) {
  const response = await axios.patch(`${URL}/services/${id}/restore`);
  return response;
}
// Get the services for a single practice based on the practice ID
export async function getServicesPracticeById(servicesPractice) {
  try {
    const response = await axios.get(
      `${URL}/services/practice/${servicesPractice}`
    );
    if (response.status === 200) {
      return response.data; // Return the 'practice' array if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching user's practice:", error);
    return null; // Handle errors appropriately
  }
}

// Get all practices for a specific service based on the service ID
export async function getPracticesByServiceId(serviceId) {
  try {
    const response = await axios.get(`${URL}/practices/service/${serviceId}`);
    if (response.status === 200) {
      return response.data; // Return the practices array if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching practices for service:", error);
    return null; // Handle errors appropriately
  }
}

// Get all users for a specific service based on the service ID
export async function getUsersByServiceId(serviceId) {
  try {
    const response = await axios.get(`${URL}/users/service/${serviceId}`);
    if (response.status === 200) {
      return response.data; // Return the users array if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching users for service:", error);
    return null; // Handle errors appropriately
  }
}

// Get the services for an array of practices based on the practice ID.
// `opts.serviceType` opts into custom codes (omit for the default services-only view).
// `opts.includeDeleted` opts into showing soft-deleted services — used by the
// settings page's "Show deleted" toggle. The picker, invoice line, and
// appointment booking flows must never pass this.
export async function getServicesByPracticeNumbers(practiceNumbers, opts = {}) {
  try {
    const params = [];
    if (opts.serviceType) params.push(`serviceType=${encodeURIComponent(opts.serviceType)}`);
    if (opts.includeDeleted) params.push(`includeDeleted=true`);
    const query = params.length ? `?${params.join("&")}` : "";
    const response = await axios.post(`${URL}/services/practices${query}`, {
      practiceNumbers,
    });

    if (response.status === 200) {
      return response.data; // Return the services array if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching services by practice numbers:", error);
    return null; // Handle errors appropriately
  }
}

// Get the services for an array of practices based on the practice ID
export async function getServicesByPracticeNumbersPublic(practiceNumbers) {
  try {
    // Make a POST request with practiceNumbers in the body
    const response = await axios.post(`${URL}/services/practices/public`, {
      practiceNumbers,
    });

    if (response.status === 200) {
      return response.data; // Return the services array if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching services by practice numbers:", error);
    return null; // Handle errors appropriately
  }
}

// Patient API functions
export async function getPatients() {
  const response = await axios.get(`${URL}/patients`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function getPatientById(id) {
  const response = await axios.get(`${URL}/patients/${id}`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function createPatient(patient) {
  const response = await axios.post(`${URL}/patients`, patient);
  return response;
}

export async function updatePatient(id, patient) {
  try {
    const response = await axios.put(`${URL}/patients/${id}`, patient, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to update patient:", error);
    throw error;
  }
}

export async function syncPatientPhone(id, patientPhone) {
  const response = await axios.patch(`${URL}/patients/${id}/sync-phone`, { patientPhone });
  return response.data;
}

export async function deletePatient(id) {
  const response = await axios.delete(`${URL}/patients/${id}`);
  return response;
}

export async function searchPatients(searchQuery, practices) {
  try {
    const response = await axios.get(`${URL}/patients/search`, {
      params: {
        searchQuery, // Send search query
        practices: practices.join(","), // Convert practices array to comma-separated string
      },
    });

    return response.data; // Return the filtered results
  } catch (error) {
    console.error("Error searching patients:", error.message);
    return [];
  }
}

// Check if the patient exists based on the ID number
export async function checkMultiplePatients(practiceNumbers, patientDOB, patientFirstName, patientLastName) {
  try {
    const response = await axios.post(`${URL}/patient/checkPatient`, {
      practiceNumbers,
      patientDOB,
      patientFirstName,
      patientLastName,
    });

    if (response.status === 200) {
      return response.data; // ✅ Return messages from API
    } else {
      throw new Error("Unexpected response status: " + response.status);
    }
  } catch (error) {
    console.error("⛔ Error checking multiple patients:", error);
    return { error: "Failed to check patients" };
  }
}

// Diagnosis API functions
export async function getPatientDiagnoses(patientId) {
  try {
    const res = await axios.get(`${URL}/patients/${patientId}/diagnoses`, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (error) {
    console.error("❌ Failed to fetch patient diagnoses:", error);
    throw error;
  }
}

// Diagnosis marked to be used on the invoice:
export async function getPatientInvoiceICDs(patientId) {
  try {
    const res = await axios.get(`${URL}/patients/${patientId}/invoice-icds`, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (error) {
    console.error("❌ Failed to fetch patient invoice ICDs:", error);
    throw error;
  }
}


export async function createPatientDiagnosis(patientId, diagnosis) {
  // diagnosis: { date: 'YYYY-MM-DD', note: '', icds: [{ item, isPrimary }] }
  try {
    const res = await axios.post(`${URL}/patients/${patientId}/diagnoses`, diagnosis, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (error) {
    console.error("❌ Failed to create diagnosis:", error);
    throw error;
  }
}

export async function updatePatientDiagnosis(diagnosisId, diagnosis) {
  // diagnosis: fields to update (date?, note?, icds?)
  try {
    const res = await axios.put(`${URL}/patient-diagnoses/${diagnosisId}`, diagnosis, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (error) {
    console.error("❌ Failed to update diagnosis:", error);
    throw error;
  }
}

export async function deletePatientDiagnosis(diagnosisId) {
  try {
    const res = await axios.delete(`${URL}/patient-diagnoses/${diagnosisId}`, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (error) {
    console.error("❌ Failed to delete diagnosis:", error);
    throw error;
  }
}

// find a patient by their medical aid dependant code
export async function getPatientIdByMedAidDepCode(medicalAidNumber, dependantCode) {
  try {
    const response = await axios.post(`${URL}/patients/byMedAidDepCode`, {
      medicalAidNumber,
      dependantCode,
    });

    // ✅ Return just the patientId string
    if (response.status === 200 && response.data?._id) {
      return response.data._id
    } else {
      console.warn("No patientId found in response:", response.data);
      return null;
    }
  } catch (error) {
    console.error("Error fetching patient by medical aid and dependant code:", error);
    return null;
  }
}

// api.js (or wherever)
export async function generatePatAccNumber(practiceNumbers) {
  try {
    const response = await axios.post(`${URL}/practice/generatePatAccNumber`, {
      practiceNumbers, // ← send array
    });

    if (response.status === 200 && response.data) {
      // { accountNumber, patientNumber }
      return {
        accountNumber: response.data.accountNumber,
        patientNumber: response.data.patientNumber,
      };
    } else {
      console.warn("Unexpected response from generatePatAccNumber:", response.data);
      return null;
    }
  } catch (error) {
    console.error("Error generating patient/account numbers:", error);
    return null;
  }
}

export async function updatePatAccNumber(practiceNumbers, patientNumber, accountNumber) {
  try {
    const response = await axios.post(
      `${URL}/practice/updatePatAccNumber`,
      {
        practiceNumbers,   // ← array of practice numbers
        patientNumber,     // ← exact string like "PAT0007"
        accountNumber,     // ← exact string like "ACC0007"
      }
    );

    if (response.status === 200 && response.data) {
      return {
        message: response.data.message,
        lastPatNumber: response.data.lastPatNumber,
        lastAccNumber: response.data.lastAccNumber,
        matchedCount: response.data.matchedCount,
        modifiedCount: response.data.modifiedCount,
      };
    } else {
      console.warn("Unexpected response from updatePatAccNumber:", response.data);
      return null;
    }
  } catch (error) {
    console.error("Error updating patient/account numbers:", error);
    return null;
  }
}

export async function getNextPatientNumber(accountNumber) {
  try {
    const token = sessionStorage.getItem("user");
    const response = await axios.post(
      `${URL}/practice/getNextPatientNumber`,
      { accountNumber },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.status === 200 && response.data) {
      return response.data; // { patientNumber }
    }
    return null;
  } catch (error) {
    console.error("Error fetching next patient number:", error);
    return null;
  }
}

export async function getNewAccountCandidates(surname, practiceNumbers) {
  try {
    const token = sessionStorage.getItem("user");
    const response = await axios.post(
      `${URL}/practice/getNewAccountCandidates`,
      { surname, practiceNumbers },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.status === 200 && response.data) {
      return response.data; // { candidates: [{ practiceNumber, method, candidate, patientNumber }] }
    }
    return null;
  } catch (error) {
    console.error("Error fetching new account candidates:", error);
    return null;
  }
}

export async function getNumberCandidates(accountId, surname) {
  try {
    const token = sessionStorage.getItem("user");
    const response = await axios.post(
      `${URL}/practice/getNumberCandidates`,
      { accountId, surname },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("getNumberCandidates error:", error?.response?.data || error.message);
    return null;
  }
}



// Account API functions
export async function getAccounts() {
  const response = await axios.get(`${URL}/accounts`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

// ✅ Fetch patients linked to a specific account by account ID
export async function getAccountPatients(accountId) {
  try {
    const response = await axios.post(`${URL}/accounts/getPatients`, {
      accountId,
    });

    if (response.status === 200) {
      return response.data; // ✅ Return patient data
    } else {
      throw new Error("Unexpected response status: " + response.status);
    }
  } catch (error) {
    console.error("⛔ Error fetching patients for account:", error);
    return { error: "Failed to fetch patients" };
  }
}

export async function getAccountById(accountId) {
  try {
    const response = await axios.post(`${URL}/accounts/byid`, {
      accountId, // Send accountId in the request body
    });

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error retrieving account:", error);
    return null;
  }
}

export async function getAccountByPatientId(patientId) {
  try {
    const response = await axios.get(`${URL}/accounts/by-patient/${patientId}`);

    if (response.status === 200) {
      return response.data; // Return the account data
    } else {
      console.error("Unexpected response status:", response.status);
      return null; // Return null if response is not OK
    }
  } catch (error) {
    console.error("Error fetching account by patient ID:", error);
    return null; // Handle errors and return null
  }
}

export async function getAccountByAccountIdNumber(id) {
  try {
    const response = await axios.get(`${URL}/accounts/by-account/${id}`);

    if (response.status === 200) {
      return response.data; // Return the account data
    } else {
      console.error("Unexpected response status:", response.status);
      return null; // Return null if response is not OK
    }
  } catch (error) {
    console.error("Error fetching account by patient ID:", error);
    return null; // Handle errors and return null
  }
}

// Get a list of accounts based on the Practice Number array of a user
export async function getAccountsByPracticeNumbers({
  practiceNumbers,
  page,
  limit,
  practiceFilter = [],
  accountTypeFilter = [],
  sortKey = "accountOutstandingAmount",
  sortDirection = "desc",
}) {
  try {
    const response = await axios.post(`${URL}/accounts/byPracticeNumbers`, {
      practiceNumbers,
      page,
      limit,
      practiceFilter,
      accountTypeFilter,
      sortKey,
      sortDirection,
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error fetching accounts:", error);
    return { accounts: [], total: 0 };
  }
}


export async function createAccount(account) {
  const response = await axios.post(`${URL}/accounts`, account);
  return response;
}

export async function updateAccount(id, account) {
  const response = await axios.put(`${URL}/accounts/${id}`, account);
  return response;
}

export async function deleteAccount(id) {
  const response = await axios.delete(`${URL}/accounts/${id}`);
  return response;
}

export async function searchAccounts(searchQuery, practices) {
  try {
    const response = await axios.get(`${URL}/accounts/search`, {
      params: {
        searchQuery, // Send search query
        practices: practices.join(","), // Convert practices array to comma-separated string
      },
    });

    return response.data; // Return the filtered results
  } catch (error) {
    console.error("Error searching accounts:", error.message);
    return [];
  }
}

export const removePatientFromAccount = async (accountId, patientId) => {
  try {
    const response = await axios.put(
      `${URL}/accounts/${accountId}/removePatient/${patientId}`
    );
    if (response.status === 200) {
      return response.data;
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error("Error removing patient from account:", error.message);
    return null;
  }
};

// Add an array of patients to an account
export const addPatientsToAccount = async (accountId, patientIds) => {
  try {
    const response = await axios.put(
      `${URL}/accounts/${accountId}/addPatients`,
      {
        patientIds,
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error("Error adding patients to account:", error.message);
    return null;
  }
};

// Remove an array of patient from an account
export const removePatientsFromAccount = async (accountId, patientIds) => {
  try {
    const response = await axios.put(
      `${URL}/accounts/${accountId}/removePatients`,
      {
        patientIds,
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error("Error removing patients from account:", error.message);
    return null;
  }
};

// Check if an Account already exists:
export async function checkMultipleAccounts(practiceNumbers, accountIdNumber, iod) {
  try {
    const response = await axios.post(`${URL}/accounts/checkAccount`, {
      practiceNumbers,
      accountIdNumber,
      iod,
    });

    if (response.status === 200) {
      return response.data; // ✅ Return messages from API
    } else {
      throw new Error("Unexpected response status: " + response.status);
    }
  } catch (error) {
    console.error("⛔ Error checking multiple accounts:", error);
    return { error: "Failed to check accounts" };
  }
}

// ✅ Check if an account exists by account number (ERA Go-to-Account fallback)
export async function checkAccountByNumber(accountNumber) {
  try {
    const response = await axios.post(`${URL}/accounts/byaccountnumber`, {
      accountNumber,
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Unexpected response status: " + response.status);
    }
  } catch (error) {
    console.error("⛔ Error checking account by account number:", error);
    return { error: "Failed to check account" };
  }
}

// ✅ Check if an account exists by Medical Aid Number
export async function checkAccountByMedicalAid(medicalAidNumber) {
  try {
    const response = await axios.post(`${URL}/accounts/bymedicalaid`, {
      medicalAidNumber, // ✅ Send in request body
    });

    if (response.status === 200) {
      return response.data; // ✅ Return the account details if found
    } else {
      throw new Error("Unexpected response status: " + response.status);
    }
  } catch (error) {
    console.error("⛔ Error checking account by Medical Aid Number:", error);
    return { error: "Failed to check account" };
  }
}

// Add patient to an Account
export async function addPatientToAccount(accountId, patientId) {
  try {
    const response = await axios.put(
      `${URL}/accounts/${accountId}/addPatient/${patientId}`
    );
    if (response.status === 200) {
      return response.data;
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error("Error adding patient to account:", error.message);
    return null;
  }
}

// Appointment API functions
export async function getAppointments() {
  const response = await axios.get(`${URL}/appointments`);

  if (response.status === 200) {
    return response.data;
  } else {
    return;
  }
}

export async function getAppointmentById(id) {
  if (!id) {
    console.error("No ID provided for getAppointmentById.");
    return null; // Return early for invalid input
  }

  try {
    const response = await axios.get(`${URL}/appointments/id/${id}`);

    // Check for successful response
    if (response.status === 200 && response.data) {
      return response.data; // Return the data on success
    }

    // Handle unexpected response status
    console.warn(
      `Unexpected response: ${response.status} - ${response.statusText}`
    );
    return null;
  } catch (error) {
    // Handle Axios-specific errors and log meaningful messages
    if (error.response) {
      console.error(
        `Error fetching appointment data: ${error.response.status} - ${error.response.statusText}`
      );
    } else if (error.request) {
      console.error("No response received from server:", error.request);
    } else {
      console.error("Unexpected error occurred:", error.message);
    }

    return null; // Return null explicitly for errors
  }
}

// Retrieve all appointments for a patient based on patientId
export async function getAppointmentsByPatientId(patientId) {
  try {
    // ✅ Validate patientId input
    if (!patientId) {
      throw new Error("Patient ID is required");
    }

    // ✅ Send POST request to the backend route with patientId in the body
    const response = await axios.post(`${URL}/appointments/patient`, {
      patientId,
    });

    // ✅ Check for a successful response
    if (response.status === 200) {
      return response.data.appointments; // Return the formatted appointments array
    } else {
      throw new Error(response.data.message || "Failed to fetch appointments");
    }
  } catch (error) {
    console.error("❌ Error fetching appointments:", error.message || error);
    throw error; // Re-throw the error for handling in the caller
  }
}

export async function createAppointment(appointment) {
  const response = await axios.post(`${URL}/appointments`, appointment);
  return response;
}


// Replace your existing function with this:
export async function createAppointmentPublic(appointment) {
  try {
    const response = await axios.post(
      `${URL}/appointments/public`,
      appointment
    );
    return response.data;
  } catch (error) {
    console.error("Error creating appointment:", error.response?.data || error.message);
    const errMsg = error.response?.data?.error;
    if (
      error.response?.status === 400 &&
      errMsg &&
      /recaptcha/i.test(errMsg)
    ) {
      throw new Error('Security verification failed. Please try again.');
    }
    throw error;
  }
}

// Create the recurring appointments:
export async function createRecurringAppointments(data) {
  try {
    const response = await axios.post(
      `${URL}/appointments/recurring-create`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error creating recurring appointments:", error);
    throw error;
  }
}

//calculate the end time of the appointment
export async function calculateAppointment(appointmentData) {
  try {
    const response = await axios.post(
      `${URL}/appointments/calculated`,
      appointmentData
    );

    if (response.status === 200) {
      return response.data; // Returns either { endTime: ... } or conflict suggestions.
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error("Error calculating appointment:", error);
    return null;
  }
}

// check the availability of a recurring appointment
export async function checkRecurringSchedule(data) {
  try {
    const response = await axios.post(
      `${URL}/appointments/recurring-check`,
      data
    );

    if (response.status === 200) {
      return response.data; // { endTime: 'HH:mm', available: [...], conflicts: [...] }
    } else {
      console.error(`Recurring check error: status ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error("Error checking recurring schedule:", error);
    return null;
  }
}

export async function updateAppointment(id, appointment) {
  const response = await axios.put(`${URL}/appointments/${id}`, appointment);
  return response;
}
// Update appointment's patientId and status by using the patient ID
export async function updateAppointmentPatientId(id, patientId, status) {
  const response = await axios.post(`${URL}/appointments/${id}/patientId`, {
    patientId,
    status,
  });
  return response;
}

// Update appointment status by patient ID
/**
 * Move an appointment to a status the application chose, by TYPE.
 *
 * Callers that mean "mark this done" no longer name a status, because a practice
 * can rename theirs. The server resolves the type against that appointment's own
 * practice, which is also the only place that reliably knows which practice it
 * belongs to.
 */
export async function updateAppointmentStatusByType(
  appointmentId,
  statusType,
  appointmentUpdatedBy
) {
  try {
    const response = await axios.put(
      `${URL}/appointments/status/${appointmentId}`,
      {
        statusType,
        appointmentUpdatedBy: appointmentUpdatedBy || "System",
      }
    );
    return response.data;
  } catch (error) {
    console.error("⛔ Error updating appointment status by type:", error);
    throw error;
  }
}

export async function updateAppointmentStatusByPatientId(
  appointmentId,
  status,
  appointmentUpdatedBy
) {
  try {
    const response = await axios.put(  // Changed from patch to put
      `${URL}/appointments/status/${appointmentId}`,
      {
        status: status,
        appointmentUpdatedBy: appointmentUpdatedBy || "System",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating appointment status:", error);
    
    if (error.response) {
      console.error("Server Error:", error.response.status, error.response.data);
      throw new Error(`Failed to update appointment: ${error.response.data.message || error.response.statusText}`);
    } else if (error.request) {
      console.error("Network Error:", error.request);
      throw new Error("Network error: Unable to reach server");
    } else {
      console.error("Error:", error.message);
      throw new Error(`Request failed: ${error.message}`);
    }
  }
}

export async function deleteAppointment(id) {
  const response = await axios.delete(`${URL}/appointments/${id}`);
  return response;
}

export async function deleteAppointmentSeries(recurringID, fromDate) {
  const response = await axios.delete(
    `${URL}/appointments/series/${recurringID}?fromDate=${encodeURIComponent(fromDate)}`
  );
  return response.data;
}

// Check if an appointment exists for a patient based on their ID number, last name, and start time
export async function checkAppointment(data) {
  const { patientIdNumber, patientLastName, start, practiceId } = data;
  if (!patientIdNumber || !patientLastName || !start || !practiceId) {
    throw new Error(
      "Missing required fields: patientIdNumber, patientLastName, start, or practiceId."
    );
  }

  // Send POST request to check for the appointment
  const response = await axios.post(`${URL}/appointments/check`, {
    patientIdNumber,
    patientLastName,
    start,
    practiceId,
  });

  if (response.status === 200) {
    return response.data; // Return the response data (e.g., { found: true })
  } else {
    console.error(`Unexpected response status: ${response.status}`);
    return { found: false }; // Return false explicitly for non-200 responses
  }
}

/**
 * Patient self check-in, from the public kiosk (/arrived/:practiceNumbers).
 *
 * One call that proves who the patient is and marks them arrived. It replaced
 * check-then-update-status: that second step went through the general-purpose
 * status endpoint, which had to stay unauthenticated for the kiosk's sake and
 * so let anyone with an appointment id set any status on any booking.
 */
export async function arriveAppointment(data) {
  const { patientIdNumber, patientLastName, start, practiceId, whatsappConsent } = data;
  if (!patientIdNumber || !patientLastName || !start || !practiceId) {
    throw new Error(
      "Missing required fields: patientIdNumber, patientLastName, start, or practiceId."
    );
  }
  try {
    const response = await axios.post(`${URL}/appointments/arrive`, {
      patientIdNumber,
      patientLastName,
      start,
      practiceId,
      // Grant-only on the server, and it never overrides a patient who has
      // opted out — this endpoint is unauthenticated.
      ...(whatsappConsent === true ? { whatsappConsent: true } : {}),
    });
    return response.data;
  } catch (error) {
    // 404 = no matching appointment, which is an ordinary outcome at a kiosk
    // (wrong surname, wrong day) rather than an error worth throwing over.
    if (error.response?.status === 404) return { found: false };
    throw error;
  }
}

/**
 * Public kiosk config — whether to show the WhatsApp opt-in at check-in.
 *
 * Returns one boolean. A failure here must never stop a patient checking in, so
 * it resolves to false rather than throwing.
 */
export async function getCheckInOptions(practiceNumbers) {
  try {
    const { data } = await axios.get(
      `${URL}/appointments/checkin-options/${practiceNumbers}`
    );
    return { askWhatsAppConsent: data?.askWhatsAppConsent === true };
  } catch (_error) {
    return { askWhatsAppConsent: false };
  }
}

// Retrieve all appointments for a practice based on the user's practice
export async function fetchUserAppointments(userId) {
  try {
    const response = await axios.get(`${URL}/appointments/${userId}`);

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch appointments"
    );
  }
}

// Retrieve all appointments for a practice based on the user's practice(s) by date
export async function fetchUserAppointmentsByDate(userId, date) {
  try {
    const response = await axios.get(
      `${URL}/appointments/date/${userId}/${date}`
    );
    return response.data; // Return the data from the response
  } catch (error) {
    // Improved error handling
    const errorMessage =
      error.response?.data?.message || "Failed to fetch appointments";
    console.error(
      `Error fetching appointments for user ${userId} on date ${date}:`,
      errorMessage
    );
    throw new Error(errorMessage);
  }
}

// Retrieve all appointments for a specific user based on the user ID
export async function fetchAppointmentsByResource(resourceId) {
  try {
    const response = await axios.get(
      `${URL}/appointments/resource/${resourceId}`
    );
    return response.data; // Return the data from the response
  } catch (error) {
    // Improved error handling
    const errorMessage =
      error.response?.data?.error || "Failed to fetch appointments";
    console.error(
      `Error fetching appointments for resource ${resourceId}:`,
      errorMessage
    );
    throw new Error(errorMessage);
  }
}

// A practice's appointment statuses — labels, colours, icons and the type that
// gives each one its behaviour. Backed by seeded defaults, so this never comes
// back empty for a practice that has not configured anything.
export async function getAppointmentStatuses(practiceNumber) {
  const { data } = await axios.get(`${URL}/appointments/statuses`, {
    params: { practiceNumber },
  });
  return data; // { statuses: [...], typeBehaviour: {...} }
}

// Return appointments for the users of the practice array within a date range, including core availability and scheduled changes
export async function getAppointmentsByPracticeIds(
  practiceIds,
  startDate,
  endDate
) {
  try {
    const response = await axios.post(`${URL}/appointments/availability`, {
      practiceIds,
      startDate,
      endDate,
    });

    if (response.status === 200) {
      return response.data; // Return the appointments and availability events
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 responses
    }
  } catch (error) {
    console.error("Error fetching appointments by practice IDs:", error);
    return null; // Handle errors appropriately
  }
}

// Return available appointment slots for the public booking tool:
export const getAvailableAppointmentSlots = async ({
  date,
  serviceDuration,
  overlapPadding,
  overlapMinutes,
  resourceId,
  selectedPractice,
  selectedLocationName,
}) => {
  try {
    if (!date || !serviceDuration || !resourceId) {
      throw new Error(
        "Missing required parameters: date, serviceDuration, or resourceId."
      );
    }

    const response = await axios.post(`${URL}/appointments/calculated-public`, {
      date,
      serviceDuration,
      overlapPadding,
      overlapMinutes,
      resourceId,
      selectedPractice,
      selectedLocationName,
    });

    if (response.status === 200) {
      return response.data; // { date: string, slots: string[] }
    } else {
      throw new Error(
        response.data.message || "Failed to fetch available slots."
      );
    }
  } catch (error) {
    console.error(
      "Error fetching available appointment slots:",
      error.message || error
    );
    throw error;
  }
};

// Retrieve all userids, userfirstnames and lastnames with the role Practioner for a list of practiceId's
export const fetchPractitionersByPractices = async (practiceIds) => {
  try {
    // Ensure the input is an array and not empty
    if (!Array.isArray(practiceIds) || practiceIds.length === 0) {
      throw new Error("Invalid or empty practiceIds array");
    }

    // Send a POST request to the backend route
    const response = await axios.post(`${URL}/users/practices`, {
      practiceIds,
    });

    // Check if the response status is 200 (OK)
    if (response.status === 200) {
      return response.data; // Return the array of practitioners
    } else {
      throw new Error(response.data.message || "Failed to fetch practitioners");
    }
  } catch (error) {
    console.error("Error fetching practitioners:", error.message || error);
    throw error; // Re-throw the error for the calling function to handle
  }
};

// Retrieve all userids, userfirstnames and lastnames with the role Practioner for a list of practiceId's
export const fetchallUsersByPractices = async (practiceIds) => {
  try {
    // Ensure the input is an array and not empty
    if (!Array.isArray(practiceIds) || practiceIds.length === 0) {
      throw new Error("Invalid or empty practiceIds array");
    }

    // Send a POST request to the backend route
    const response = await axios.post(`${URL}/practiceusers/practices`, {
      practiceIds,
    });

    // Check if the response status is 200 (OK)
    if (response.status === 200) {
      return response.data; // Return the array of practitioners
    } else {
      throw new Error(response.data.message || "Failed to fetch practitioners");
    }
  } catch (error) {
    console.error("Error fetching practitioners:", error.message || error);
    throw error; // Re-throw the error for the calling function to handle
  }
};

export const getPractitionersByPracticeNumbersPublic = async (practiceIds) => {
  try {
    // Ensure the input is an array and not empty
    if (!Array.isArray(practiceIds) || practiceIds.length === 0) {
      throw new Error("Invalid or empty practiceIds array");
    }

    // Send a POST request to the backend route
    const response = await axios.post(`${URL}/practiceusers/practices/public`, {
      practiceIds,
    });

    // Check if the response status is 200 (OK)
    if (response.status === 200) {
      return response.data; // Return the array of practitioners
    } else {
      throw new Error(response.data.message || "Failed to fetch practitioners");
    }
  } catch (error) {
    console.error("Error fetching practitioners:", error.message || error);
    throw error; // Re-throw the error for the calling function to handle
  }
};

// Retrieve all services for a practice based on the userId.
// `opts.includeDeleted` keeps soft-deleted services in the result so the
// EditAppointments form can still resolve historical appointment refs by id.
export const fetchUserServices = async (userId, opts = {}) => {
  try {
    // Validate userId input
    if (!userId) {
      throw new Error("User ID is required");
    }

    const query = opts.includeDeleted ? "?includeDeleted=true" : "";
    const response = await axios.get(`${URL}/services/user/${userId}${query}`);

    // Check for successful response
    if (response.status === 200) {
      return response.data; // Return the list of services
    } else {
      throw new Error(response.data.message || "Failed to fetch user services");
    }
  } catch (error) {
    console.error("Error fetching user services:", error.message || error);
    throw error; // Re-throw the error for the caller to handle
  }
};

// Create a session
export async function createSession(payload) {
  try {
    const response = await axios.post(`${URL}/sessions/create/`, payload);

    return response.data; // Return only the data
  } catch (error) {
    console.error(
      "Error creating session:",
      error.response?.data || error.message
    );
    throw error; // Propagate the error for handling in the calling code
  }
}

// Retrieve all sessions for a patient based on the patientId
export const fetchPatientSessions = async (patientId) => {
  if (!patientId) throw new Error("patient ID is required");

  try {
    const res = await axios.get(
      `${URL}/sessions/patient/${encodeURIComponent(patientId)}`
    );

    // Normalize success payloads
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    const status = err?.response?.status;

    // No sessions found → just return []
    if (status === 404 || status === 204) {
      return [];
    }

    // Other errors bubble up
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch sessions";
    console.error("Error fetching sessions:", msg);
    throw new Error(msg);
  }
};

// Retrieve all sessions for a list of practices and an optional patientId (default: "1" - unlinked)
export async function fetchSessionsByPractices(practiceIds, patientIdFilter = "1") {
  const { data } = await axios.post(
    `${URL}/sessions/practices/unlinked`,
    { practiceIds, patientId: patientIdFilter }
  );
  return data?.sessions || [];
}

// Linking a patient to sessions and appointments in bulk
export async function bulkLinkSessionsAndAppointments({ sessionIds, appointmentIds = [], patientId, forceNumber = false }) {
  const { data } = await axios.post(
    `${URL}/sessions/bulk/patient`,
    { sessionIds, appointmentIds, patientId, forceNumber }
  );
  return data;
}


export async function getSessionById(sessionId) {
  try {
    const { data } = await axios.get(`${URL}/sessions/${sessionId}`);
    return data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch session ${sessionId}`
    );
  }
}


//Create a template
export async function createTemplate(templateData) {
  try {
    const response = await axios.post(`${URL}/templates/`, templateData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to create template"
    );
  }
}

// ✅ Update a template by ID
export async function updateTemplate(templateId, templateData) {
  try {
    const response = await axios.put(
      `${URL}/templates/${templateId}`,
      templateData
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to update template"
    );
  }
}

// Publish a template — POSTs to /templates/:id/versions to create an immutable
// version snapshot and set status Active (backend handles both in one request)
export async function publishTemplate(templateId) {
  try {
    const response = await axios.post(`${URL}/templates/${templateId}/versions`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || "Failed to publish template");
  }
}

export async function fetchTemplateVersions(templateId) {
  try {
    const response = await axios.get(`${URL}/templates/${templateId}/versions`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || "Failed to fetch template versions");
  }
}

export async function fetchTemplateVersion(templateId, versionNumber) {
  try {
    const response = await axios.get(`${URL}/templates/${templateId}/versions/${versionNumber}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || "Failed to fetch template version");
  }
}

// Retrieve all templates for a practice based on the user's practice
export async function fetchPracticeTemplates(userId) {
  try {
    const response = await axios.get(`${URL}/templates/${userId}`);

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch appointments"
    );
  }
}

// Retrieve a single template based on the template ID
export async function fetchTemplateById(id) {
  try {
    const response = await axios.get(`${URL}/templates/id/${id}`, {});

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch template"
    );
  }
}

// Live per-status appointment counts for one patient.
//
// Deliberately not read from the clinical-memory document: those figures are a
// by-product of the last AI build, so they lag reality, and they only exist as
// four behaviour buckets rather than the practice's own status names.
export async function getPatientAppointmentStatusCounts(patientId, practiceId) {
  try {
    const response = await axios.get(
      `${URL}/appointments/patient/${patientId}/status-counts`,
      { params: practiceId ? { practiceId: String(practiceId) } : {} },
    );
    return response.data; // { total, counts: [{ key, label, color, icon, count }] }
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to load appointment counts"
    );
  }
}

// Delete a template. The backend refuses library templates and templates
// belonging to a practice the caller is not part of.
export async function deleteTemplate(id) {
  try {
    const response = await axios.delete(`${URL}/templates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || error.response?.data?.message || "Failed to delete template"
    );
  }
}

// Change a template's status without going through a full save.
export async function setTemplateStatus(id, templateStatus) {
  try {
    const response = await axios.patch(`${URL}/templates/status/${id}`, { templateStatus });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || error.response?.data?.message || "Failed to update template status"
    );
  }
}

/* ── Template library ──────────────────────────────────────────────────────
 * System templates authored by a SystemAdmin. Practices preview them and take
 * one by adopting it, which copies it into the practice — there is no link
 * back, so a later library change never touches an adopted copy.
 */

// List the library. `includeAll` asks for Drafts and retired templates too and
// is only honoured for SystemAdmins; everyone else gets the published set.
export async function fetchTemplateLibrary(includeAll = false) {
  try {
    const response = await axios.get(
      `${URL}/templates/library${includeAll ? "?status=all" : ""}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to fetch the template library"
    );
  }
}

// Single library template including pages[], for preview and for editing.
export async function fetchLibraryTemplate(id) {
  try {
    const response = await axios.get(`${URL}/templates/library/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to fetch the library template"
    );
  }
}

export async function createLibraryTemplate(templateData) {
  try {
    const response = await axios.post(`${URL}/templates/library`, templateData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to create library template"
    );
  }
}

export async function updateLibraryTemplate(id, templateData) {
  try {
    const response = await axios.put(`${URL}/templates/library/${id}`, templateData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to update library template"
    );
  }
}

export async function setLibraryTemplateStatus(id, templateStatus) {
  try {
    const response = await axios.patch(`${URL}/templates/library/${id}/status`, { templateStatus });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to update library template status"
    );
  }
}

export async function deleteLibraryTemplate(id) {
  try {
    const response = await axios.delete(`${URL}/templates/library/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to delete library template"
    );
  }
}

// Copy an existing practice template into the library as a Draft. The source
// template is left exactly as it is.
export async function promoteTemplateToLibrary(sourceId) {
  try {
    const response = await axios.post(`${URL}/templates/library/promote/${sourceId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to promote template to the library"
    );
  }
}

// Copy a library template into the given practices. Returns
// { templateId, templateTitle } for the new Draft.
export async function adoptLibraryTemplate(id, templatePracticeId) {
  try {
    const response = await axios.post(`${URL}/templates/library/${id}/adopt`, { templatePracticeId });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Failed to adopt the template"
    );
  }
}

// Export a completed session's template as a PDF (backend-generated)
// options: { includeTemplateTitle, includePatientName, includeIdNumber, includeDob, includeMedicalAid, includeSessionDate, templateTitle? }
// Defaults are all true on the backend; only false flags are appended as query params.
// templateTitle (optional, string) overrides the title rendered on the PDF —
// the backend falls back to the stored template title when blank.
export async function exportSessionTemplatePdf(sessionId, options) {
  const token = sessionStorage.getItem("user");
  const params = {};
  if (options && typeof options === "object") {
    for (const [k, v] of Object.entries(options)) {
      if (k === "templateTitle") {
        if (typeof v === "string" && v.trim()) params.templateTitle = v.trim();
      } else if (v === false) {
        params[k] = "false";
      }
    }
  }
  const response = await axios.get(`${URL}/templates/pdf/session/${sessionId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    responseType: "blob",
    params,
  });
  const blob = new Blob([response.data], { type: "application/pdf" });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  link.download = match ? match[1] : "SessionNotes.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

// Email a completed session's template PDF via Azure
// options: same shape as exportSessionTemplatePdf — sent in the POST body under `options`.
export async function emailSessionTemplatePdf(sessionId, { to, cc, subject, message, options }) {
  const token = sessionStorage.getItem("user");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const { data } = await axios.post(
    `${URL}/templates/email/session/${sessionId}`,
    { to, cc, subject, message, options: options || {} },
    { headers }
  );
  return data;
}

// Update sessions by sessionId
export async function updateSessionById(sessionId, session) {
  try {
    const response = await axios.post(
      `${URL}/sessions/update/${sessionId}`,
      session
    );
    return response.data; // Return the data from the response
  } catch (error) {
    console.error("Error updating session:", error.message);
    throw error; // Rethrow the error to handle it in the caller
  }
}

// Retrieve all appointments for a practice based on the user's practice
export async function fetchSessionByAppointmentId(appointmentId) {
  try {
    const response = await axios.get(
      `${URL}/sessions/appointment/${appointmentId}`
    );

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch appointments"
    );
  }
}

/**
 * Every note on an appointment — the note rail's data source.
 *
 * Returns light rows (kind, title, timestamps, content flags), not full note
 * bodies; the editor loads the one note it opens via getSessionById. An
 * appointment with no notes yields [], which is not an error.
 */
export async function fetchNotesByAppointmentId(appointmentId) {
  try {
    const response = await axios.get(
      `${URL}/sessions/appointment/${appointmentId}/list`
    );
    return Array.isArray(response.data?.notes) ? response.data.notes : [];
  } catch (error) {
    console.error("Error fetching appointment notes:", error);
    return [];
  }
}

/** Delete one note. Used by the note rail's per-row delete. */
export async function deleteSessionById(sessionId) {
  try {
    const response = await axios.delete(`${URL}/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting session:", error);
    throw error;
  }
}

// Function to fetch availability by user ID
export async function getAvailabilityByUserId(userId) {
  try {
    const response = await axios.get(`${URL}/availability/by-user/${userId}`);

    if (response.status === 200) {
      return response.data; // Return the availability data if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching user's availability:", error);
    return null; // Handle errors appropriately
  }
}

// Function to create availability for a user
export async function createAvailability({
  userId,
  fromDate,
  toDate,
  workingDays,
}) {
  try {
    const response = await axios.post(`${URL}/availability`, {
      userId,
      fromDate,
      toDate,
      workingDays,
    });

    if (response.status === 201) {
      return response.data; // ✅ Return the created availability data
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // ✅ Handle non-201 status codes
    }
  } catch (error) {
    console.error("Error creating availability:", error);
    return null; // ✅ Handle errors appropriately
  }
}

// Function to update an existing availability record by ID
export async function updateAvailability(
  userId,
  fromDate,
  toDate,
  workingDays
) {
  try {
    const payload = {
      userId,
      fromDate,
      toDate,
      workingDays,
    };

    const response = await axios.put(`${URL}/availability/${userId}`, payload);

    if (response.status === 200) {
      return response.data; // Return the updated availability data
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error updating availability:", error);
    return null; // Handle errors appropriately
  }
}

// Function to fetch availability by user ID
export async function getAvailabilityChangeByUserId(userId) {
  try {
    const response = await axios.get(
      `${URL}/availability/change/by-user/${userId}`
    );

    if (response.status === 200) {
      return response.data; // Return the availability data if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching user's availability:", error);
    return null; // Handle errors appropriately
  }
}

// Function to fetch availability by user ID
export async function getAvailabilityChangeByUserIdDate(userId, date) {
  try {
    const response = await axios.get(
      `${URL}/availability/change/by-user/${userId}/${date}`
    );

    if (response.status === 200) {
      return response.data; // Return the availability data if successful
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error fetching user's availability:", error);
    return null; // Handle errors appropriately
  }
}

// Function to create availability for a user
export async function createAvailabilityChange(userId, workingDays) {
  try {
    const response = await axios.post(`${URL}/availability/change`, {
      userId,
      workingDays,
    });

    if (response.status === 201) {
      return response.data; // Return the created availability data
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-201 status codes
    }
  } catch (error) {
    console.error("Error creating availability:", error);
    return null; // Handle errors appropriately
  }
}

// Function to update an existing availability record by ID
export async function updateAvailabilityChange(availabilityId, workingDays) {
  try {
    const response = await axios.put(
      `${URL}/availability/change/${availabilityId}`,
      {
        workingDays,
      }
    );

    if (response.status === 200) {
      return response.data; // Return the updated availability data
    } else {
      console.error(`Error: Received status code ${response.status}`);
      return null; // Handle non-200 status codes
    }
  } catch (error) {
    console.error("Error updating availability:", error);
    return null; // Handle errors appropriately
  }
}

//upload an image to blobStorage
export async function uploadImage(formData) {
  try {
    const compressed = await compressFormDataImages(formData);
    const response = await axios.post(`${URL}/fileStorage`, compressed);
    return response.data; // ✅ Returns `{ message, imageUrl }`
  } catch (error) {
    console.error(
      "⛔ Error uploading image:",
      error.response?.data?.message || error.message
    );
    throw new Error(error.response?.data?.message || "Failed to upload image");
  }
}

//remove an image from blobStorage
export async function deleteImage(fileName) {
  try {
    const response = await axios.delete(`${URL}/fileStorage/${fileName}`, {});

    return response.data; // ✅ Returns `{ message }`
  } catch (error) {
    console.error(
      "Error deleting image:",
      error.response?.data?.message || error.message
    );
    throw new Error(error.response?.data?.message || "Failed to delete image");
  }
}

export async function generateProgressReport(reportData) {
  const token = sessionStorage.getItem("user"); // ✅ Ensure token is retrieved properly

  if (!token) {
    throw new Error(
      "No authentication token found. User may not be logged in."
    );
  }

  try {
    const response = await axios.post(`${URL}/generate-report`, reportData, {
      headers: {
        Authorization: `Bearer ${token}`, // ✅ Ensure correct header format
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      "⛔ API Request Error:",
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message || "Failed to generate report"
    );
  }
}

export async function sendAppointmentConfirmationEmail(appointment) {
  try {
    const response = await axios.post(
      `${URL}/appointments/sendConfirmationEmail`,
      { appointment }
    );

    return response.data;
  } catch (error) {
    console.error("⛔ Error sending appointment confirmation email:", error);
    return {
      error:
        error.response?.data?.error ||
        "Failed to send appointment confirmation email",
    };
  }
}

// create Account Notes
export async function createAccountNotes(data) {
  try {
    const response = await axios.post(
      `${URL}/notesHistory/create`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error creating recurring appointments:", error);
    throw error;
  }
}

// create Account Notes
export async function updateAccountNotes(id, data) {
  try {
    const response = await axios.post(
      `${URL}/notesHistory/update/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error creating recurring appointments:", error);
    throw error;
  }
}

// api.js
export async function getHistoryNotes(accountId) {
  try {
    const response = await axios.post(`${URL}/notesHistory/account`, { accountId });
    return response.data;          // <-- important!
  } catch (error) {
    console.error("⛔ Error fetching account notes:", error);
    throw error;                   // let the caller catch it
  }
}

// Update the per-account tag (short coloured label). Pass empty tagText to clear.
export async function updateAccountTag(accountId, tagText, tagColor) {
  try {
    const response = await axios.post(`${URL}/account/tag`, {
      accountId,
      tagText,
      tagColor,
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error updating account tag:", error);
    throw error;
  }
}

export async function sendAppointmentEmail(appointmentData) {
  const response = await axios.post("/api/email/sendAppointmentConfirmation", appointmentData);
  return response.data;
}

export async function fetchPatientFiles(patientId) {
  try {
    const response = await axios.get(
      `${URL}/fileStorage/patient/${patientId}`,
    );
    return response.data; // array
  } catch (error) {
    console.error("⛔ Error fetching patient files:", error);
    throw error;
  }
}

export async function uploadPatientFile({ file, caption, patientId }) {
  try {
    const fd = new FormData();
    fd.append("image", file);

    const response = await axios.post(`${URL}/fileStorage`, fd, {
      headers: {
        "x-image-caption": caption || "",
        "x-patient-id": patientId,
      },
    });

    return response.data; // { imageUrl, fileName, ... }
  } catch (error) {
    console.error("⛔ Error uploading patient file:", error);
    return {
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to upload file",
    };
  }
}

export async function deletePatientFile(fileName) {
  try {
    const response = await axios.delete(
      `${URL}/fileStorage/${encodeURIComponent(fileName)}`
    );
    return response.data;
  } catch (error) {
    console.error("⛔ Error deleting patient file:", error);
    throw error;
  }
}

export async function updatePatientFileCaption(fileName, caption) {
  try {
    const response = await axios.put(
      `${URL}/fileStorage/${encodeURIComponent(fileName)}`,
      { caption }
    );
    return response.data;
  } catch (error) {
    console.error("⛔ Error updating file caption:", error);
    throw error;
  }
}

// ============================================
// Bulk Statement Run API Functions
// ============================================

/**
 * Get eligible accounts for bulk statement run
 * @param {Object} params - { practiceNumbers, page, limit, search, sortKey, sortDir }
 * @returns {Promise<Object>} { total, page, limit, accounts }
 */
export async function getEligibleAccounts(params) {
  try {
    const queryParams = new URLSearchParams({
      practiceNumbers: params.practiceNumbers.join(","),
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || "",
      sortKey: params.sortKey || "totalOutstanding",
      sortDir: params.sortDir || "desc",
      ...(params.month && { month: params.month }),
      ...(params.idsOnly && { idsOnly: "true" }),
    });

    const response = await axios.get(
      `${URL}/statement-runs/eligible-accounts?${queryParams}`
    );
    return response.data;
  } catch (error) {
    console.error("⛔ Error fetching eligible accounts:", error);
    throw error;
  }
}

/**
 * Create a new statement run
 * @param {Object} data - { month, practiceNumbers, selectedAccountIds, settings }
 * @returns {Promise<Object>} { message, runId, itemCount }
 */
export async function createStatementRun(data) {
  try {
    const response = await axios.post(`${URL}/statement-runs`, data);
    return response.data;
  } catch (error) {
    console.error("⛔ Error creating statement run:", error);
    throw error;
  }
}

/**
 * Get list of statement runs
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} { total, page, limit, runs }
 */
export async function getStatementRuns(page = 1, limit = 20) {
  try {
    const response = await axios.get(`${URL}/statement-runs`, {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error fetching statement runs:", error);
    throw error;
  }
}

/**
 * Get details of a specific statement run
 * @param {string} id - Run ID
 * @returns {Promise<Object>} Run details with counts
 */
export async function getStatementRun(id) {
  try {
    const response = await axios.get(`${URL}/statement-runs/${id}`);
    return response.data;
  } catch (error) {
    console.error("⛔ Error fetching statement run:", error);
    throw error;
  }
}

/**
 * Get items for a statement run
 * @param {string} id - Run ID
 * @param {Object} params - { status, page, limit }
 * @returns {Promise<Object>} { total, page, limit, items }
 */
export async function getStatementRunItems(id, params = {}) {
  try {
    const response = await axios.get(`${URL}/statement-runs/${id}/items`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error fetching statement run items:", error);
    throw error;
  }
}

/**
 * Cancel a statement run
 * @param {string} id - Run ID
 * @returns {Promise<Object>} { message }
 */
export async function cancelStatementRun(id) {
  try {
    const response = await axios.post(`${URL}/statement-runs/${id}/cancel`);
    return response.data;
  } catch (error) {
    console.error("⛔ Error cancelling statement run:", error);
    throw error;
  }
}

/**
 * Retry failed items in a statement run
 * @param {string} id - Run ID
 * @returns {Promise<Object>} { message, retriedCount }
 */
export async function getMedicalAidsReference() {
  const token = sessionStorage.getItem("user");
  const response = await axios.get(`${URL}/datamigration/medicalaids-reference`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 200) return response.data;
  return [];
}

export async function retryFailedItems(id) {
  try {
    const response = await axios.post(`${URL}/statement-runs/${id}/retry-failed`);
    return response.data;
  } catch (error) {
    console.error("⛔ Error retrying failed items:", error);
    throw error;
  }
}

// ─── Dashboard Tasks (Phase 1 — SyncTo-generated) ─────────────────────────
// All endpoints under /api/tasks. Auth header is attached globally by
// BootNav.jsx (axios.defaults.headers.common.authorization), so individual
// helpers don't repeat it.

function tasksAuthHeader() {
  const token = sessionStorage.getItem("user");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Tasks list. `view` is one of 'mine' | 'mentions' | 'all' | 'done'.
// Server returns { tasks, page, limit, totalCount, hasAny, view, isSuperAdmin }.
// hasAny lets the dashboard keep the section mounted across filter changes
// even when the current view is empty.
export async function listMyTasks({
  view = 'mine',
  limit = 50,
  page = 1,
} = {}) {
  try {
    const response = await axios.get(`${URL}/api/tasks`, {
      params: { view, limit, page },
      headers: tasksAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("⛔ Error loading tasks:", error);
    return { tasks: [], page: 1, limit, totalCount: 0, hasAny: false, view, isSuperAdmin: false };
  }
}

export async function getTask(taskId) {
  try {
    const response = await axios.get(`${URL}/api/tasks/${taskId}`, {
      headers: tasksAuthHeader(),
    });
    return response.data; // { task, comments }
  } catch (error) {
    console.error("⛔ Error loading task:", error);
    return null;
  }
}

export async function addTaskComment(taskId, { commentText, mentionedUserIds, parentCommentId } = {}) {
  const url = parentCommentId
    ? `${URL}/api/tasks/${taskId}/comments/${parentCommentId}/replies`
    : `${URL}/api/tasks/${taskId}/comments`;
  const response = await axios.post(url, {
    commentText,
    mentionedUserIds: Array.isArray(mentionedUserIds) ? mentionedUserIds : [],
    parentCommentId: parentCommentId || null,
  }, { headers: tasksAuthHeader() });
  return response.data; // { comment, task, mentionedUserIds }
}

// Manual task creation (Phase 2 — user-created, no SyncTo origin).
// Body fields are optional except description and assignedToUserId.
export async function createTask({
  description,
  assignedToUserId,
  mentionedUserIds = [],
  patientId = null,
  patientName = null,
  appointmentId = null,
} = {}) {
  const response = await axios.post(
    `${URL}/api/tasks`,
    {
      description,
      assignedToUserId,
      mentionedUserIds: Array.isArray(mentionedUserIds) ? mentionedUserIds : [],
      patientId,
      patientName,
      appointmentId,
    },
    { headers: tasksAuthHeader() },
  );
  return response.data; // { task }
}

export async function patchTaskStatus(taskId, status) {
  const response = await axios.patch(
    `${URL}/api/tasks/${taskId}/status`,
    { status },
    { headers: tasksAuthHeader() },
  );
  return response.data; // { task }
}

export async function patchTaskAssignee(taskId, assignedToUserId) {
  const response = await axios.patch(
    `${URL}/api/tasks/${taskId}/assignee`,
    { assignedToUserId: assignedToUserId || null },
    { headers: tasksAuthHeader() },
  );
  return response.data; // { task }
}

export async function markTaskRead(taskId) {
  try {
    await axios.post(`${URL}/api/tasks/${taskId}/read`, {}, { headers: tasksAuthHeader() });
  } catch (_e) { /* silent — read marker is best-effort */ }
}

// Upload one or more image attachments to a task. `files` is a FileList or
// array of File objects. Each is run through compressImage (1600px / q=0.85)
// before upload — originals never hit the wire. Returns the server's array
// of new attachment records (each with a fresh SAS-signed URL the caller can
// drop straight into <img src=…>).
export async function uploadTaskAttachments(taskId, files) {
  const list = Array.isArray(files) ? files : Array.from(files || []);
  if (!list.length) return { attachments: [] };
  const fd = new FormData();
  for (const f of list) {
    fd.append('attachments', f, f.name || 'image.jpg');
  }
  // Run images through the shared compression utility before sending.
  const compressed = await compressFormDataImages(fd);
  const response = await axios.post(
    `${URL}/api/tasks/${taskId}/attachments`,
    compressed,
    { headers: { ...tasksAuthHeader() } }, // axios sets multipart Content-Type itself
  );
  return response.data; // { attachments: [...] }
}

export async function listPracticeUsers() {
  try {
    const response = await axios.get(`${URL}/api/tasks/practice-users`, {
      headers: tasksAuthHeader(),
    });
    return response.data?.users || [];
  } catch (error) {
    console.error("⛔ Error loading practice users:", error);
    return [];
  }
}

// ─── Patient Clinical Memory ─────────────────────────────────────────────
// Cached AI-generated clinical history per (patientId, practiceId).
// Server: backend/src/patientClinicalMemoryRoutes.js.

export async function getPatientClinicalMemory(patientId, practiceId) {
  const params = practiceId ? { practiceId } : {};
  const response = await axios.get(`${URL}/api/clinical-memory/${patientId}`, {
    params,
    headers: tasksAuthHeader(),
  });
  return response.data;
}

/**
 * GET /api/clinical-memory/:patientId/status — the poll target.
 *
 * Returns only the indexing state. The full document above carries one AI
 * summary per session and is unbounded in practice, so polling it to read a
 * single status string is what turned a rebuild into tens of megabytes of
 * egress. Poll this; re-fetch the full document once, when it settles.
 */
export async function getPatientClinicalMemoryStatus(patientId, practiceId) {
  const params = practiceId ? { practiceId } : {};
  const response = await axios.get(
    `${URL}/api/clinical-memory/${patientId}/status`,
    { params, headers: tasksAuthHeader() },
  );
  return response.data;
}

export async function refreshPatientClinicalMemory(patientId, practiceId) {
  const params = practiceId ? { practiceId } : {};
  const response = await axios.post(
    `${URL}/api/clinical-memory/${patientId}/refresh`,
    {},
    { params, headers: tasksAuthHeader() },
  );
  return response.data;
}


// ── Terms & Conditions ───────────────────────────────────────────────────────

/**
 * GET /terms/current — the current Terms plus whether this user has accepted
 * this version. The document travels with the version so what was accepted can
 * always be reproduced.
 */
export async function getCurrentTerms() {
  const { data } = await axios.get(`${URL}/terms/current`);
  return data;
}

/** POST /terms/accept — record acceptance. 409 means the Terms moved on. */
export async function acceptTerms(version) {
  const { data } = await axios.post(`${URL}/terms/accept`, { version });
  return data;
}
